import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ticketAPI, marketingAPI, passengerAPI, hotelRateAPI, freeNightAPI, draftAPI, checkinAPI, companyAPI, dashboardAPI, paymentAPI, visaAPI, weeklyDutyAPI, announcementAPI, hrAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../utils/dateFormat';
import ActivityFeed from '../components/ActivityFeed';
import OtpMiniFeed from '../components/OtpMiniFeed';
import HaberMini from '../components/HaberMini';
import { HR_GROUPS, canSeeHRItem } from '../config/hrPageGroups';
import { canUseWidget } from './DashboardManagement.widgetPerms';
import './Dashboard.css';

// V71 — tek biçim: kısa tarih gg.aa, para 1.234,56 ₺ / EUR
const fmtDM = (d) => (d ? fmtDate(d).split('.').slice(0, 2).join('.') : '');
const fmtMoney = (v, cur = 'TRY') =>
  `${Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur === 'TRY' ? '₺' : cur}`;

/* ── TCMB Ticker ── */
const TcmbTicker = () => {
  const [rates, setRates] = useState([]);
  useEffect(() => {
    dashboardAPI.getTcmbRates().then(r => {
      const main = ['USD','EUR','GBP','CHF','JPY','SAR','AED','CAD','AUD','RUB','CNY','NOK','SEK','DKK','KWD','QAR'];
      const filtered = (r.data.rates || []).filter(x => main.includes(x.code));
      setRates(filtered);
    }).catch(() => {});
  }, []);
  if (!rates.length) return null;
  const items = [...rates, ...rates]; // duplicate for infinite scroll
  return (
    <div className="dash-ticker-wrap">
      <span className="dash-ticker-label">TCMB</span>
      <div className="dash-ticker-track">
        <div className="dash-ticker-scroll">
          {items.map((r, i) => (
            <span key={i} className="dash-ticker-item">
              <strong>{r.code}</strong>
              <span className="dash-ticker-buy">A: {r.buy.toFixed(4)}</span>
              <span className="dash-ticker-sell">S: {r.sell.toFixed(4)}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Announcement 3D Flip Carousel ── */
const AnnouncementFlip = ({ items }) => {
  const [idx, setIdx] = useState(0);
  const [flip, setFlip] = useState(false);
  const [detail, setDetail] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (items.length <= 1) return;
    timerRef.current = setInterval(() => {
      setFlip(true);
      setTimeout(() => {
        setIdx(prev => (prev + 1) % items.length);
        setFlip(false);
      }, 400);
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, [items.length]);

  const a = items[idx];
  if (!a) return null;
  const getIcon = (t) => t === 'warning' ? '⚠️' : t === 'success' ? '✅' : t === 'birthday' ? '🎂' : '📢';

  return (
    <>
      <div className="dash-announcements">
        <div className={`dash-announce dash-announce-${a.type} ${flip ? 'dash-announce-flip' : ''}`}
          style={{ cursor: 'pointer' }} onClick={() => setDetail(a)} role="button" tabIndex={0}
          onKeyDown={e => { if (e.key === 'Enter') setDetail(a); }}>
          <span className="dash-announce-icon">{getIcon(a.type)}</span>
          <span className="dash-announce-content">
            <strong>{a.title}</strong>
            {a.body && <span className="dash-announce-body">{a.body}</span>}
          </span>
          {items.length > 1 && (
            <span className="dash-announce-dots">
              {items.map((_, i) => (
                <span key={i} className={`dash-announce-dot ${i === idx ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); setIdx(i); }} />
              ))}
            </span>
          )}
        </div>
      </div>
      {detail && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" style={{ overflow: 'hidden' }}>
            <div className={`dash-announce-modal-header dash-announce-modal-${detail.type}`}>
              <div style={{ fontSize: 38 }}>{getIcon(detail.type)}</div>
              <h2 style={{ margin: '6px 0 0', fontSize: 17, fontWeight: 700 }}>{detail.title}</h2>
            </div>
            <div style={{ padding: '18px 24px' }}>
              {detail.body ? (
                <p style={{ fontSize: 14, color: 'var(--fg-primary)', lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap' }}>{detail.body}</p>
              ) : (
                <p style={{ fontSize: 13, color: 'var(--fg-tertiary)', margin: 0 }}>Ek detay yok.</p>
              )}
            </div>
            <div style={{ padding: '10px 24px 18px', textAlign: 'center' }}>
              <button className="btn btn-primary" style={{ padding: '8px 36px' }} onClick={() => setDetail(null)}>Tamam</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* ── World Clock — V71: kullanıcının Dashboard Ayarları'nda seçtiği şehirler (/dashboard/world-weather);
   uç yanıt vermezse 6 sabit lokasyon yedeği ── */
const CLOCK_CITIES = [
  { name: 'IST', label: 'Istanbul Airport', tz: 'Europe/Istanbul', lat: 41.01, lon: 28.98 },
  { name: 'LHR', label: 'Heathrow', tz: 'Europe/London', lat: 51.47, lon: -0.46 },
  { name: 'CDG', label: 'Charles de Gaulle', tz: 'Europe/Paris', lat: 49.01, lon: 2.55 },
  { name: 'BER', label: 'Brandenburg', tz: 'Europe/Berlin', lat: 52.52, lon: 13.40 },
  { name: 'DXB', label: "Dubai Int'l", tz: 'Asia/Dubai', lat: 25.25, lon: 55.36 },
  { name: 'MIA', label: "Miami Int'l", tz: 'America/New_York', lat: 25.80, lon: -80.29 },
];

const WMO_ICONS = {
  0: '☀️', 1: '🌤️', 2: '⛅', 3: '☁️',
  45: '🌫️', 48: '🌫️',
  51: '🌦️', 53: '🌦️', 55: '🌧️',
  61: '🌧️', 63: '🌧️', 65: '🌧️',
  71: '🌨️', 73: '🌨️', 75: '🌨️', 77: '🌨️',
  80: '🌦️', 81: '🌧️', 82: '🌧️',
  85: '🌨️', 86: '🌨️',
  95: '⛈️', 96: '⛈️', 99: '⛈️',
};

// Sunucuda hava anahtarı yoksa open-meteo yedeği (koordinatı bilinen şehirler)
const fetchOpenMeteo = async (cities) => {
  const geo = cities.filter(c => c.lat != null && c.lon != null);
  if (!geo.length) return {};
  const res = await fetch(
    `https://api.open-meteo.com/v1/forecast?latitude=${geo.map(c => c.lat).join(',')}&longitude=${geo.map(c => c.lon).join(',')}&current=temperature_2m,weather_code&forecast_days=1`
  );
  const data = await res.json();
  const arr = Array.isArray(data) ? data : [data];
  const w = {};
  arr.forEach((d, i) => {
    if (d?.current) w[geo[i].name] = { temp: Math.round(d.current.temperature_2m), icon: WMO_ICONS[d.current.weather_code] || '🌡️' };
  });
  return w;
};

const WorldClockWeather = () => {
  const [cities, setCities] = useState(CLOCK_CITIES);
  const [times, setTimes] = useState({});
  const [weather, setWeather] = useState({});
  const timerRef = useRef(null);

  useEffect(() => {
    const tick = () => {
      const t = {};
      cities.forEach(c => {
        try {
          t[c.name] = new Date().toLocaleTimeString('tr-TR', { timeZone: c.tz, hour: '2-digit', minute: '2-digit' });
        } catch { t[c.name] = '--:--'; }
      });
      setTimes(prev => {
        if (Object.keys(prev).length !== Object.keys(t).length) return t;
        for (const k in t) if (prev[k] !== t[k]) return t;
        return prev;
      });
    };
    tick();
    timerRef.current = setInterval(tick, 15000);
    return () => clearInterval(timerRef.current);
  }, [cities]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      let list = CLOCK_CITIES;
      try {
        const r = await dashboardAPI.getWorldWeather();
        const got = (Array.isArray(r.data) ? r.data : []).filter(c => c && c.name && c.tz);
        if (got.length) list = got;
      } catch { /* uç yoksa sabit şehirler */ }
      if (!alive) return;
      setCities(list);
      const w = {};
      list.forEach(c => {
        if (c.temp != null) w[c.name] = { temp: Math.round(c.temp), icon: WMO_ICONS[c.weatherCode] || '🌡️' };
      });
      if (Object.keys(w).length) { setWeather(w); return; }
      try {
        const om = await fetchOpenMeteo(list);
        if (alive) setWeather(om);
      } catch { /* hava durumu isteğe bağlı */ }
    };
    load();
    const iv = setInterval(load, 10 * 60 * 1000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  return (
    <div className="dash-clock-strip">
      {cities.map((c) => (
        <span key={c.name} className="dash-clock-chip" title={c.label || c.name}>
          <span className="dash-clock-code">{c.name}</span>
          <span className="dash-clock-time">{times[c.name] || '--:--'}</span>
          {weather[c.name] && <span className="dash-clock-wx">{weather[c.name].icon}{weather[c.name].temp}°</span>}
        </span>
      ))}
    </div>
  );
};

const Dashboard = () => {
  const { hasPermission, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const [stats, setStats] = useState({
    customers: 0, tickets: 0, expiring: [], passportExpiring: [], marketing: {},
    hotelRates: {}, freeNights: {}, drafts: {}, checkins: [], checkinStats: {},
    pendingCompanies: 0, totalCompanies: 0,
    todaysPayments: { total_try: 0, count: 0, items: [] },
    paymentStats: {},
    visaStats: {}
  });
  // V71 — hangi veri geldi / hata verdi: KPI'lar yüklenirken "…", hata olursa "—" gösterir (sahte 0 yok)
  const [loaded, setLoaded] = useState({});
  // V71.13.21 — Dashboard'da üst çubuk bandın ilk satırına yerleşir (CSS: html[data-aurora-top], TopBar.css)
  useEffect(() => {
    document.documentElement.setAttribute('data-aurora-top', '');
    return () => document.documentElement.removeAttribute('data-aurora-top');
  }, []);

  // V71.13.15 — son düzen tarayıcıda saklanır: yenilemede önce tüm widget'lar çizilip sonra kapananlar
  // kaybolmasın (üst bant büyüyüp küçülüyordu). Önbellek yoksa düzen gelene kadar widget'lar beklenir.
  const layoutKey = `dash_layout_${user?.id || 'x'}`;
  const [layout, setLayout] = useState(() => { try { return JSON.parse(localStorage.getItem(layoutKey)) || null; } catch { return null; } });
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  const [weeklyDuty, setWeeklyDuty] = useState([]);
  const [nextDuty, setNextDuty] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [popupAnnouncements, setPopupAnnouncements] = useState([]);
  const [myBirthday, setMyBirthday] = useState(null);

  // V71 — modül kapıları (rota korumalarıyla aynı anahtarlar)
  const canCheckin = hasPermission('checkin', 'view') || hasPermission('tickets', 'view'); // backend checkins.js: checkin|tickets
  const canCompanies = hasPermission('companies', 'view');
  // Nöbet kartı → /HR/weekly-duty: İK portalının o sayfa için kullandığı kuralla aynı
  const dutyGroup = HR_GROUPS.find(g => g.items.some(i => i.to === '/HR/weekly-duty'));
  const dutyItem = dutyGroup?.items.find(i => i.to === '/HR/weekly-duty');
  const canDuty = isAdmin || (dutyItem ? canSeeHRItem(dutyItem, dutyGroup.kind, hasPermission, isAdmin) : hasPermission('weekly_duty', 'view'));

  const isWidgetVisible = (key) => {
    if (!canUseWidget(key, hasPermission)) return false; // V71 — yetki kapısı ayar ekranlarıyla tek kaynaktan
    if (!layout) return layoutLoaded;
    const w = layout.find(l => l.key === key);
    return w ? w.visible : true;
  };

  // V71 — yetki/oturum tazeleme TEK SEFER: önceki sürümde veri effect'inin içindeydi; refreshUser yeni user
  // nesnesi yazdıkça hasPermission kimliği değişip effect'i (ve ~20 isteği) sonsuz döngüde tekrarlatıyordu
  useEffect(() => {
    refreshUser?.();
    dashboardAPI.getMyLayout().then(r => {
      const w = r.data.widgets || null;
      setLayout(w);
      try { if (w) localStorage.setItem(layoutKey, JSON.stringify(w)); else localStorage.removeItem(layoutKey); } catch { /* depolama kapalı */ }
    }).catch(() => {}).finally(() => setLayoutLoaded(true));
    // eslint-disable-next-line
  }, []);

  // Veri effect'i yalnız yetki kümesi GERÇEKTEN değişince yeniden çalışır
  const permSig = isAdmin ? 'admin' : JSON.stringify(user?.permissions || []);

  useEffect(() => {
    // V69.4 — Progressive loading: hiçbir fetch'i await etmeden hepsini fire-and-forget tetikle.
    const upd = (patch) => {
      setStats(prev => ({ ...prev, ...patch }));
      setLoaded(prev => {
        const next = { ...prev };
        Object.keys(patch).forEach(k => { next[k] = true; });
        return next;
      });
    };
    const fail = (key) => () => {
      setLoaded(prev => ({ ...prev, [key]: 'err' }));
      toast.warn('Bazı panel verileri yüklenemedi', { toastId: 'dash-load-err' });
    };

    if (hasPermission('customers', 'view')) {
      passengerAPI.getAll({ limit: 1 }).then(r => upd({ customers: r.data?.total || 0 })).catch(fail('customers'));
      draftAPI.getStats().then(r => upd({ drafts: r.data || {} })).catch(fail('drafts'));
      passengerAPI.getPassportExpiring().then(r => upd({ passportExpiring: r.data || [] })).catch(fail('passportExpiring'));
    }
    if (hasPermission('tickets', 'view')) {
      // V70.222 — açık biletler TRIS'e taşındıysa Aktif Bilet kartı gösterilmez (sayı TRIS'te)
      ticketAPI.getAll({ status: 'active', limit: 1 }).then(r => upd({ tickets: r.data?.tris_tasindi_at ? null : (r.data?.total || 0) })).catch(fail('tickets'));
      ticketAPI.getExpiring().then(r => upd({ expiring: r.data || [] })).catch(fail('expiring'));
    }
    // V71 — check-in kartı 'checkin' ya da 'tickets' yetkisiyle (backend ile aynı)
    if (canCheckin) {
      checkinAPI.getUpcoming().then(r => upd({ checkins: r.data || [] })).catch(fail('checkins'));
      checkinAPI.getStats().then(r => upd({ checkinStats: r.data || {} })).catch(fail('checkinStats'));
    }
    if (hasPermission('marketing', 'view')) marketingAPI.getStats().then(r => upd({ marketing: r.data || {} })).catch(fail('marketing'));
    if (hasPermission('hotel_rates', 'view')) hotelRateAPI.getStats().then(r => upd({ hotelRates: r.data || {} })).catch(fail('hotelRates'));
    if (hasPermission('free_nights', 'view')) freeNightAPI.getStats().then(r => upd({ freeNights: r.data || {} })).catch(fail('freeNights'));
    if (hasPermission('payments', 'view')) {
      paymentAPI.getToday().then(r => upd({ todaysPayments: r.data || { total_try: 0, count: 0, items: [] } })).catch(fail('todaysPayments'));
      paymentAPI.getStats().then(r => upd({ paymentStats: r.data || {} })).catch(fail('paymentStats'));
    }
    if (hasPermission('visas', 'view')) visaAPI.getStats().then(r => upd({ visaStats: r.data || {} })).catch(fail('visaStats'));
    // V71 — firma KPI'ı /companies rotasıyla aynı yetki (yalnız admin değil)
    if (hasPermission('companies', 'view')) {
      companyAPI.getAll({ limit: 1 }).then(r => upd({ totalCompanies: r.data?.total || 0 })).catch(fail('totalCompanies'));
    }

    // Weekly duty
    weeklyDutyAPI.getCurrent().then(r => {
      setWeeklyDuty(r.data.data || []);
      setNextDuty(r.data.next || []);
    }).catch(() => {});
    announcementAPI.getActive().then(r => {
      const all = r.data.data || [];
      // V71 — önce gelen doğum günü duyuruları silinmesin (yarış)
      setAnnouncements(prev => [...prev.filter(a => a.type === 'birthday'), ...all]);
      try {
        const seen = JSON.parse(localStorage.getItem('seenAnnouncements') || '[]');
        const unseen = all.filter(a => !seen.includes(a.id));
        if (unseen.length > 0) setPopupAnnouncements(unseen);
      } catch { /* ignore */ }
    }).catch(() => {});
    hrAPI.getBirthdaysToday().then(r => {
      const bdays = r.data.data || [];
      if (bdays.length > 0) {
        const bdayAnnouncements = bdays.map(b => ({
          id: `bday-${b.id}`,
          type: 'birthday',
          title: `🎂 Bugün ${b.name}'nın Doğum Günü!`,
          body: `Doğum günün kutlu olsun! 🎉🥳🎈`,
        }));
        setAnnouncements(prev => [...bdayAnnouncements, ...prev.filter(a => a.type !== 'birthday')]);
        const todayKey = new Date().toISOString().slice(0, 10);
        const bdaySeen = sessionStorage.getItem('bdayPopupSeen');
        if (bdaySeen !== todayKey) {
          setPopupAnnouncements(prev => [...prev, ...bdayAnnouncements]);
          sessionStorage.setItem('bdayPopupSeen', todayKey);
        }
      }
    }).catch(() => {});
    hrAPI.getMyBirthdayToday().then(r => {
      if (r.data.isBirthday) {
        const todayKey = new Date().toISOString().slice(0, 10);
        const myBdaySeen = sessionStorage.getItem('myBdayPopupSeen');
        if (myBdaySeen !== todayKey) {
          setMyBirthday(r.data.name);
          sessionStorage.setItem('myBdayPopupSeen', todayKey);
        }
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, [permSig]);

  // KPI değeri: yüklenene kadar "…", hata olursa "—"
  const kv = (key, val) => (loaded[key] === true ? val : loaded[key] === 'err' ? '—' : '…');

  const dutyEmoji = (name) => {
    const n = (name || '').toLocaleLowerCase('tr-TR');
    if (n.includes('anıl') || n.includes('anil')) return '👨‍💼';
    if (n.includes('mustafa')) return '👨‍💼';
    if (n.includes('büşra') || n.includes('busra')) return '👩';
    if (n.includes('pınar') || n.includes('pinar')) return '👱‍♀️';
    return '👤';
  };

  // V71 — KPI ikonları gradyan rozet (grup renk kimliği: Uçak/Finans/Vize; Yolcu cyan)
  const statCards = [
    hasPermission('customers', 'view') && { value: kv('customers', stats.customers), label: 'Toplam Yolcu', chip: 'cyan', to: '/customers', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
    hasPermission('tickets', 'view') && stats.tickets !== null && { value: kv('tickets', stats.tickets), label: 'Aktif Bilet', chip: 'ucak', to: '/tickets', sub: stats.expiring.length > 0 ? `${stats.expiring.length} yaklaşan` : null, icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M13 5v2M13 17v2M13 11v2"/></svg> },
    // Otel Anlaşması KPI kartı kaldırıldı (V61)
    hasPermission('payments', 'view') && { value: kv('paymentStats', (stats.paymentStats.overdue_count || 0) + (stats.paymentStats.due_today_count || 0)), label: 'Ödeme Takip', chip: 'finmuh', sub: [stats.paymentStats.overdue_count && `${stats.paymentStats.overdue_count} gecikmiş`, stats.paymentStats.due_today_count && `${stats.paymentStats.due_today_count} bugün`].filter(Boolean).join(' · ') || null, icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg> },
    hasPermission('visas', 'view') && { value: kv('visaStats', stats.visaStats.total || 0), label: 'Vize İşlemi', chip: 'vize', to: '/visas', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg> },
    canCompanies && { value: kv('totalCompanies', stats.totalCompanies || 0), label: 'Toplam Firma', chip: 'finmuh', to: '/companies', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4"/><path d="M9 9h1M9 13h1M9 17h1"/></svg> },
  ].filter(Boolean);

  // Nöbetçi kartları — üst satırda ayrı gösterilecek; İK sayfasına erişim yoksa tıklanamaz
  const dutyCards = (() => {
    if (!isWidgetVisible('weekly_duty')) return [];
    return weeklyDuty.map(d => {
      const next = nextDuty.find(nd => nd.department === d.department);
      return {
        value: d.staff_name?.split(' ')[0], label: d.department === 'bilet' ? '✈️ Nöbetçi' : '🏨 Nöbetçi',
        chip: 'amber', to: canDuty ? '/HR/weekly-duty' : undefined,
        sub: next ? `» ${next.staff_name?.split(' ')[0]}` : null,
        icon: <span style={{ fontSize: 14 }}>{dutyEmoji(d.staff_name)}</span>,
      };
    });
  })();

  // V71 — "Uyarı Bildirimleri" zil menüsüne taşındı (TopBar); ölü alerts hesabı kaldırıldı

  const summaryCards = [
    isWidgetVisible('summary_marketing') && hasPermission('marketing', 'view') && {
      title: 'Pazarlama',
      to: '/marketing',
      items: [
        { dot: 'var(--temp-hot)', label: 'Sıcak', value: stats.marketing.hot || 0 },
        { dot: 'var(--temp-warm)', label: 'Ilık', value: stats.marketing.warm || 0 },
        { dot: 'var(--temp-cold)', label: 'Soğuk', value: stats.marketing.cold || 0 },
      ]
    },
    isWidgetVisible('summary_hotels') && hasPermission('hotel_rates', 'view') && {
      title: 'Otel Anlaşmaları',
      to: '/hotel-rates',
      items: [
        { dot: 'var(--success-600)', label: 'Aktif', value: stats.hotelRates.active || 0 },
        { dot: 'var(--warning-500)', label: 'Sor-Sat', value: stats.hotelRates.sor_sat || 0 },
        { dot: 'var(--slate-400)', label: 'Süresi Dolmuş', value: stats.hotelRates.expired || 0 },
      ]
    },
    isWidgetVisible('summary_freenights') && hasPermission('free_nights', 'view') && {
      title: 'Ücretsiz Geceler',
      to: '/free-nights',
      items: [
        { dot: 'var(--success-600)', label: 'Aktif', value: stats.freeNights.active_count || 0 },
        { dot: 'var(--warning-500)', label: 'Kısmi', value: stats.freeNights.partial_count || 0 },
        { dot: '#7c3aed', label: 'Kalan', value: stats.freeNights.total_remaining || 0 },
      ]
    },
    // V71 — /api/visas/stats düz {open, in_progress, done} döner (byStatus yoktu → kart hiç görünmüyordu)
    isWidgetVisible('summary_visas') && hasPermission('visas', 'view') && stats.visaStats.total != null && {
      title: 'Vize İşlemleri',
      to: '/visas',
      items: [
        { dot: 'var(--info-500)', label: 'Açık', value: stats.visaStats.open || 0 },
        { dot: 'var(--warning-500)', label: 'İşlemde', value: stats.visaStats.in_progress || 0 },
        { dot: 'var(--success-600)', label: 'Tamamlandı', value: stats.visaStats.done || 0 },
      ]
    },
    // V71.26 — Kart İşlemleri özeti kaldırıldı (kart işlemleri TRIS'e geçti)
  ].filter(Boolean);

  // V71.12.1 — Aurora düzeni: üst bant (selam + nöbet + kur/saat + KPI), altta ana alan + sağ canlı sütun
  const now = new Date();
  const todayStr = `${now.toLocaleDateString('tr-TR', { weekday: 'long' })} · ${now.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}`;
  const hr = now.getHours();
  const greet = hr < 12 ? 'Günaydın' : hr < 18 ? 'İyi günler' : 'İyi akşamlar';
  const firstName = (user?.full_name || '').split(' ')[0];
  const kpiCards = isWidgetVisible('stat_chips') ? statCards : [];
  const showOtp = isWidgetVisible('otp_feed_mini') && hasPermission('otp_feed_widget', 'view');
  const showActivity = isWidgetVisible('activity_feed');
  const showNews = isWidgetVisible('news_feed'); // V71.32
  // V71.12.2 — verisi olan tablo yoksa canlı kartlar ana alana yayılır (dar sağ sütun + boş alan olmasın)
  const tableCount = [
    canCheckin && stats.checkins.length > 0 && isWidgetVisible('checkin_widget'),
    hasPermission('tickets', 'view') && stats.expiring.length > 0 && isWidgetVisible('expiring_tickets'),
    hasPermission('customers', 'view') && stats.passportExpiring.length > 0 && isWidgetVisible('passport_expiring'),
    hasPermission('payments', 'view') && stats.todaysPayments.count > 0 && isWidgetVisible('todays_payments'),
  ].filter(Boolean).length;
  const spread = tableCount === 0;
  const hasRail = (!spread && announcements.length > 0) || showOtp || showNews || showActivity;
  const go = (to) => (to ? { onClick: () => navigate(to), role: 'link', tabIndex: 0, onKeyDown: e => { if (e.key === 'Enter') navigate(to); } } : {});

  return (
    <>
      <div className="page-container dash-compact dash-c au-page">
        <section className="au-band">
          <div className="au-band-row">
            <div>
              <span className="au-eyebrow">{todayStr}</span>
              <h1 className="au-title">{greet}{firstName ? `, ${firstName}` : ''}</h1>
            </div>
            {dutyCards.length > 0 && (
              <div className="au-actions">
                {dutyCards.map((d, i) => (
                  <span key={i} className={`au-pill${d.to ? ' link' : ''}`} {...go(d.to)}>
                    {d.icon}<b>{d.value}</b><small>{d.label}{d.sub ? ` ${d.sub}` : ''}</small>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="dash-bar1">
            <TcmbTicker />
            {isWidgetVisible('world_clock_weather') && <WorldClockWeather />}
          </div>
          {kpiCards.length > 0 && (
            <div className="au-kpis">
              {kpiCards.map((s, i) => (
                <div key={i} className={`au-kpi${s.to ? '' : ' static'}`} {...go(s.to)}>
                  <span className="au-kpi-head"><span className="au-kpi-icon">{s.icon}</span>{s.label}</span>
                  <span className="au-kpi-value">{s.value}</span>
                  <span className="au-kpi-sub">{s.sub || '\u00a0'}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className={`dash-c-body${hasRail ? '' : ' no-rail'}${spread ? ' spread' : ''}`}>
          <div className="dash-c-main">
            {spread && announcements.length > 0 && <AnnouncementFlip items={announcements} />}
            {/* Summary mini-strips */}
            {summaryCards.length > 0 && (
              <div className="dash-summary-strip">
                {summaryCards.map((card, ci) => (
                  <div key={ci} className="dash-strip-card" onClick={() => navigate(card.to)} role="link" tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter') navigate(card.to); }}>
                    <span className="dash-strip-title">{card.title}</span>
                    <span className="dash-strip-items">
                      {card.items.map((item, ii) => (
                        <span key={ii} className="dash-strip-item">
                          <span className="dash-sum-dot" style={{ background: item.dot }}></span>
                          {item.label} <strong>{item.value}</strong>
                        </span>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            )}

        {/* Table widgets — sorted by layout order */}
        <div className="dash-widget-grid">
          {(() => {
            const TABLE_WIDGETS = ['checkin_widget', 'expiring_tickets', 'passport_expiring', 'todays_payments'];
            const widgetOrder = TABLE_WIDGETS.map(key => {
              const idx = layout ? layout.findIndex(l => l.key === key) : -1;
              return { key, order: idx >= 0 ? layout[idx].order : TABLE_WIDGETS.indexOf(key) };
            }).sort((a, b) => a.order - b.order).map(w => w.key);

            return widgetOrder.map(key => {
              if (!isWidgetVisible(key)) return null;

              if (key === 'checkin_widget' && canCheckin && stats.checkins.length > 0) return (
                <div key={key} className="p-card p-card-clickable" onClick={() => navigate('/checkins?tab=calendar')} role="link" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' && e.target === e.currentTarget) navigate('/checkins?tab=calendar'); }}>
                  <div className="p-card-header">
                    <h3>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--fg-brand)" strokeWidth="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                      Yaklaşan Check-in
                    </h3>
                    <span className="badge badge-warning">{stats.checkins.length}</span>
                  </div>
                  <div className="p-card-body">
                    {stats.checkins.map(c => (
                      <div key={c.id} className="dash-ci-row">
                        <span className="dash-ci-date">{fmtDM(c.flight_date)}</span>
                        <span className="dash-ci-time">{c.flight_time || '-'}</span>
                        <span className="dash-ci-name">{c.passenger_name}</span>
                        <span className="badge-airline" style={{ fontSize: 10 }}>{c.airline}</span>
                        <span className="dash-ci-route">{c.route_raw || ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );

              if (key === 'expiring_tickets' && hasPermission('tickets', 'view') && stats.expiring.length > 0) return (
                <div key={key} className="p-card p-card-clickable" onClick={() => navigate('/tickets')} role="link" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' && e.target === e.currentTarget) navigate('/tickets'); }}>
                  <div className="p-card-header">
                    <h3>Yaklaşan Açık Bilet</h3>
                    <span className="badge badge-expired">{stats.expiring.length}</span>
                  </div>
                  <div className="p-card-body">
                    <table className="data-table dash-table">
                      <thead><tr><th>Yolcu</th><th>Güzergah</th><th>PNR</th><th>Son Tarih</th></tr></thead>
                      <tbody>
                        {stats.expiring.map(t => {
                          const name = (t.first_name && t.last_name) ? `${t.first_name} ${t.last_name}` : t.passenger_name || '-';
                          const isExpired = new Date(t.expiry_date) < new Date();
                          return (
                            <tr key={t.id}>
                              <td><strong>{name}</strong></td>
                              <td>{t.route_from && t.route_to ? `${t.route_from}→${t.route_to}` : t.route_raw || '-'}</td>
                              <td style={{ fontSize: 11, color: 'var(--fg-secondary)' }}>{t.pnr_code || '-'}</td>
                              <td><span style={{ color: isExpired ? 'var(--danger-600)' : 'var(--warning-600)', fontWeight: 600 }}>{t.expiry_date ? fmtDM(t.expiry_date) : '-'}</span></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );

              if (key === 'passport_expiring' && hasPermission('customers', 'view') && stats.passportExpiring.length > 0) return (
                <div key={key} className="p-card p-card-clickable" onClick={() => navigate('/customers?filter=passport-expiring')} role="link" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' && e.target === e.currentTarget) navigate('/customers?filter=passport-expiring'); }}>
                  <div className="p-card-header">
                    <h3>Pasaportu Dolacaklar</h3>
                    <span className="badge badge-expired">{stats.passportExpiring.length}</span>
                  </div>
                  <div className="p-card-body">
                    <table className="data-table dash-table">
                      <thead><tr><th>Yolcu</th><th>Pasaport No</th><th>Bitiş</th><th>Kalan</th></tr></thead>
                      <tbody>
                        {stats.passportExpiring.map((p, i) => (
                          <tr key={`${p.id}-${i}`}>
                            <td><strong>{p.first_name} {p.last_name}</strong></td>
                            <td>{p.passport_no_masked}</td>
                            <td><span className={`badge ${p.expired ? 'badge-expired' : 'badge-active'}`}>{fmtDate(p.expiry_date)}</span></td>
                            <td><span style={{ color: p.expired ? 'var(--danger-600)' : 'var(--warning-600)', fontWeight: 600 }}>{p.expired ? `${Math.abs(p.days_left)}g geçmiş` : `${p.days_left}g`}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );

              if (key === 'todays_payments' && hasPermission('payments', 'view') && stats.todaysPayments.count > 0) return (
                <div key={key} className="p-card">
                  <div className="p-card-header">
                    <h3>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--danger-600)" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                      Bugünkü Ödemeler
                    </h3>
                    <span className="badge badge-expired">{stats.todaysPayments.count}</span>
                  </div>
                  <div className="p-card-body">
                    <div style={{ padding: '10px 16px', background: 'var(--danger-50)', borderBottom: '1px solid var(--danger-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: 'var(--danger-700)', fontWeight: 500 }}>Toplam</span>
                      <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--danger-600)', fontFamily: 'var(--font-mono)' }}>{fmtMoney(stats.todaysPayments.total_try)}</span>
                    </div>
                    <table className="data-table dash-table">
                      <thead><tr><th>Tedarikçi</th><th>Açıklama</th><th className="num">Tutar (₺)</th></tr></thead>
                      <tbody>
                        {stats.todaysPayments.items.map(p => (
                          <tr key={p.id}>
                            <td><strong>{p.vendor_name}</strong></td>
                            <td style={{ fontSize: 12, color: 'var(--fg-secondary)' }}>{p.description || '-'}</td>
                            <td className="num" style={{ fontWeight: 600, color: 'var(--danger-600)' }}>
                              {p.approved_amount_try != null && p.approved_amount_try < p.remaining_try
                                ? <><span style={{ fontSize: 10, color: 'var(--fg-tertiary)', textDecoration: 'line-through', marginRight: 4 }}>{Number(p.remaining_try).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>{Number(p.approved_amount_try).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</>
                                : Number(p.remaining_try).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );

              return null;
            });
          })()}

        </div>
          </div>
          {hasRail && (
            <aside className="dash-c-rail">
              {!spread && announcements.length > 0 && <AnnouncementFlip items={announcements} />}
          {/* ── OTP Mini Feed Widget ── */}
          {showOtp && (
            <div className="p-card">
              <div className="p-card-header">
                <h3>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--fg-brand)" strokeWidth="2"><path d="M12 1l3 6 6 1-4.5 4 1 6-5.5-3-5.5 3 1-6L3 8l6-1 3-6z"/></svg>
                  Son Şifreler
                  <span className="dash-live-label"><span className="dash-live-dot"></span>Canlı</span>
                </h3>
              </div>
              <div className="p-card-body">
                <OtpMiniFeed limit={6} />
              </div>
            </div>
          )}

          {showNews && <HaberMini />}

          {/* ── Activity Feed Widget ── */}
          {showActivity && (
            <div className="p-card">
              <div className="p-card-header">
                <h3>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--fg-brand)" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                  Aktivite Akışı
                  <span className="dash-live-label"><span className="dash-live-dot"></span>Canlı</span>
                </h3>
              </div>
              <div className="p-card-body">
                <ActivityFeed maxItems={25} />
              </div>
            </div>
          )}
            </aside>
          )}
        </div>
      </div>
      {/* ─── ANNOUNCEMENT POPUP MODAL ──────────────────────── */}
      {popupAnnouncements.length > 0 && (
        <div className="modal-overlay" onClick={() => {
          // Mark as seen
          try {
            const seen = JSON.parse(localStorage.getItem('seenAnnouncements') || '[]');
            const ids = popupAnnouncements.map(a => a.id).filter(id => typeof id === 'number');
            localStorage.setItem('seenAnnouncements', JSON.stringify([...new Set([...seen, ...ids])]));
          } catch { /* ignore */ }
          setPopupAnnouncements([]);
        }}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" style={{ overflow: 'hidden' }}>
            <div className="dash-announce-popup-header">
              <div style={{ fontSize: 40, marginBottom: 4 }}>📢</div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Yeni Duyurular</h2>
            </div>
            <div style={{ padding: '16px 20px', maxHeight: 360, overflowY: 'auto' }}>
              {popupAnnouncements.map((a, i) => (
                <div key={a.id || i} className={`dash-announce dash-announce-${a.type || 'info'}`} style={{ marginBottom: 8, animation: 'none' }}>
                  <span className="dash-announce-icon">{a.type === 'warning' ? '⚠️' : a.type === 'success' ? '✅' : a.type === 'birthday' ? '🎂' : '📢'}</span>
                  <span className="dash-announce-content">
                    <strong>{a.title}</strong>
                    {a.body && <span className="dash-announce-body">{a.body}</span>}
                  </span>
                </div>
              ))}
            </div>
            <div style={{ padding: '12px 20px 16px', textAlign: 'center' }}>
              <button className="btn btn-primary" style={{ padding: '10px 40px', fontWeight: 600 }} onClick={() => {
                try {
                  const seen = JSON.parse(localStorage.getItem('seenAnnouncements') || '[]');
                  const ids = popupAnnouncements.map(a => a.id).filter(id => typeof id === 'number');
                  localStorage.setItem('seenAnnouncements', JSON.stringify([...new Set([...seen, ...ids])]));
                } catch { /* ignore */ }
                setPopupAnnouncements([]);
              }}>
                Tamam, Anladım 👍
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ─── BIRTHDAY CELEBRATION POPUP (for the birthday person) ──── */}
      {myBirthday && (
        <div className="modal-overlay" onClick={() => setMyBirthday(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" style={{ overflow: 'hidden' }}>
            <div className="dash-bday-modal-header">
              <div className="dash-bday-confetti">🎉🎈🎂🎈🎉</div>
              <h2 style={{ margin: '8px 0 4px', fontSize: 20, fontWeight: 800 }}>Doğum Günün Kutlu Olsun!</h2>
              <p style={{ margin: 0, fontSize: 14, opacity: 0.9 }}>{myBirthday}</p>
            </div>
            <div style={{ padding: '20px 24px', textAlign: 'center' }}>
              <p style={{ fontSize: 15, color: 'var(--fg-primary)', lineHeight: 1.7, margin: '0 0 8px' }}>
                Tüm MTU Agent HUB ekibi olarak sana harika bir yıl diliyoruz!
              </p>
              <p style={{ fontSize: 28, margin: '8px 0 0' }}>🥳🎊🎁🎊🥳</p>
            </div>
            <div style={{ padding: '0 24px 20px', textAlign: 'center' }}>
              <button className="btn btn-primary" style={{ padding: '10px 40px', fontWeight: 700, fontSize: 14 }} onClick={() => setMyBirthday(null)}>
                Teşekkürler! 🎉
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;
